import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/app/Layout";
import { statusTone } from "@/lib/format";
import { toast } from "sonner";
import { Copy, ShieldCheck, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";

const ROLES = ["SuperAdmin", "Manager", "Freelancer"];

function UserCount({ label, used, testid }) {
  return (
    <div className="border border-black/10 bg-white rounded-2xl p-4" data-testid={testid}>
      <div className="label-caps text-royal-800">{label}</div>
      <div className="font-display text-3xl text-royal-800 mt-1">{used}</div>
    </div>
  );
}

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [seats, setSeats] = useState(null);
  const [lastLink, setLastLink] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteRole, setInviteRole] = useState("Manager");
  const [invite, setInvite] = useState({ name: "", email: "", designation: "" });

  const load = async () => { const { data } = await api.get("/users"); setItems(data); };
  const loadSeats = async () => { try { const { data } = await api.get("/seats"); setSeats(data); } catch { setSeats(null); } };
  useEffect(() => { load(); loadSeats(); }, []);

  const setStatus = async (u, status) => {
    try { await api.patch(`/users/${u.id}`, { status }); load(); toast.success("Updated"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const changeRole = async (u, role) => {
    try { await api.patch(`/users/${u.id}`, { role }); load(); toast.success("Role updated"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const reinvite = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reinvite`);
      const link = window.location.origin + data.invite_link;
      setLastLink(link);
      toast.success("Re-invite link created"); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    try {
      const endpoint = inviteRole === "TeamLeader" ? "/users/invite-team-leader" : "/users/invite-manager";
      const payload = inviteRole === "TeamLeader" ? invite : { name: invite.name, email: invite.email };
      const { data } = await api.post(endpoint, payload);
      const link = window.location.origin + data.invite_link;
      setLastLink(link);
      toast.success(`${inviteRole === "TeamLeader" ? (invite.designation || "Operation Head") : "Manager"} invited. Share the invite link.`);
      setInvite({ name: "", email: "", designation: "" });
      setInviteOpen(false);
      load(); loadSeats();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const canDelete = ["SuperAdmin", "TeamLeader"].includes(user.role);
  const [deleteFor, setDeleteFor] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const confirmDelete = async () => {
    if (!deleteFor) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/users/${deleteFor.id}`);
      toast.success(`${deleteFor.name || deleteFor.email} removed`);
      setDeleteFor(null); load(); loadSeats();
    } catch (err) { toast.error(formatApiError(err)); }
    setDeleteBusy(false);
  };

  const canInvite = true;
  const blockReason = "";

  return (
    <div>
      <PageHeader
        title="Users"
        description="Super Admin control — invite managers, manage roles and re-issue invites."
        actions={
          <div className="flex gap-2">
            <button
              data-testid="invite-manager-btn"
              disabled={!canInvite}
              title={blockReason}
              onClick={() => { if (canInvite) { setInviteRole("Manager"); setInviteOpen(true); } else if (blockReason) toast.error(blockReason); }}
              className={`px-4 py-2 text-sm rounded-full font-semibold inline-flex items-center gap-2 ${canInvite ? "bg-royal-800 text-cream hover:bg-royal-700" : "bg-neutral-300 text-neutral-600 cursor-not-allowed"}`}>
              {canInvite ? <ShieldCheck size={14} /> : <Lock size={14} />}
              {canInvite ? "Invite Manager" : "Invites locked"}
            </button>
            <button
              data-testid="invite-team-leader-btn"
              disabled={!canInvite}
              title={blockReason}
              onClick={() => { if (canInvite) { setInviteRole("TeamLeader"); setInviteOpen(true); } else if (blockReason) toast.error(blockReason); }}
              className={`px-4 py-2 text-sm rounded-full font-semibold inline-flex items-center gap-2 ${canInvite ? "bg-gold text-ink hover:bg-gold/90" : "bg-neutral-300 text-neutral-600 cursor-not-allowed"}`}>
              <ShieldCheck size={14} /> Invite Team Leader
            </button>
          </div>
        }
      />

      <div className="p-8 space-y-6">
        {seats && (
          <div data-testid="seats-grid" className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="border border-royal-800/20 bg-royal-800 text-cream rounded-2xl p-4" data-testid="user-limit-card">
              <div className="label-caps text-cream/70">User Limit</div>
              <div className="font-display text-3xl mt-1" data-testid="user-limit-value">Unlimited</div>
            </div>
            <UserCount label="Super Admin" used={seats.used.super_admin} testid="count-super-admin" />
            <UserCount label="Operation Head" used={seats.used.team_leader} testid="count-team-leader" />
            <UserCount label="Manager" used={seats.used.manager} testid="count-manager" />
            <UserCount label="Freelancer" used={seats.used.freelancer} testid="count-freelancer" />
          </div>
        )}

        {lastLink && (
          <div data-testid="latest-invite-link" className="border border-royal-800/20 bg-white p-3 rounded-2xl">
            <div className="label-caps mb-1 text-royal-800">Latest invite link</div>
            <div className="flex items-center gap-2">
              <code className="text-xs break-all flex-1 font-mono-tight">{lastLink}</code>
              <button onClick={() => { navigator.clipboard.writeText(lastLink); toast.success("Copied"); }} className="border border-black/15 bg-white p-1.5 rounded-md"><Copy size={14} /></button>
            </div>
          </div>
        )}

        <div className="border border-neutral-200 bg-white overflow-x-auto rounded-2xl">
          <table className="fms-table w-full" data-testid="users-table">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Availability</th><th>Actions</th></tr></thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td>{u.name}</td>
                  <td className="text-sm text-neutral-600">{u.email}</td>
                  <td>
                    <select data-testid={`role-select-${u.id}`} value={u.role} onChange={(e) => changeRole(u, e.target.value)} className="border border-neutral-300 rounded-md px-2 py-1 text-xs font-mono-tight bg-white">
                      {ROLES.map((r) => <option key={r}>{r}</option>)}
                    </select>
                  </td>
                  <td><span className={`px-2 py-0.5 text-xs font-mono-tight uppercase ${statusTone(u.status)}`}>{u.status}</span></td>
                  <td className="text-xs font-mono-tight text-neutral-600">{u.availability_status || "—"}</td>
                  <td className="whitespace-nowrap space-x-3">
                    {u.status !== "deactivated" && <button data-testid={`deactivate-${u.id}`} onClick={() => setStatus(u, "deactivated")} className="text-xs underline underline-offset-4 text-[#B02A00]">Deactivate</button>}
                    {u.status === "deactivated" && <button data-testid={`activate-${u.id}`} onClick={() => setStatus(u, "active")} className="text-xs underline underline-offset-4">Activate</button>}
                    <button data-testid={`reinvite-${u.id}`} onClick={() => reinvite(u)} className="text-xs underline underline-offset-4">Re-invite</button>
                    {canDelete && u.role !== "SuperAdmin" && u.id !== user.id && (
                      <button data-testid={`delete-user-${u.id}`} onClick={() => setDeleteFor(u)} className="text-xs underline underline-offset-4 text-red-600 hover:text-red-700 font-semibold">Delete</button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="text-center text-neutral-500 py-8">No teammates yet. Invite your first Manager once you complete payment.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!deleteFor} onOpenChange={(o) => !o && !deleteBusy && setDeleteFor(null)}>
        <DialogContent className="rounded-[1.25rem] max-w-md" data-testid="delete-user-dialog">
          {deleteFor && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-red-600">Delete {deleteFor.name || deleteFor.email}?</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="text-ink/70">This is <b>permanent</b>. The user will lose all access immediately and be removed from your workspace. Their seat is freed and can be used for a new invite.</div>
                <ul className="list-disc pl-5 text-xs text-ink/60 space-y-1">
                  <li>Role: <b>{deleteFor.role}</b> · Email: <span className="font-mono-tight">{deleteFor.email}</span></li>
                  <li>Projects previously assigned to them stay in place (owner-unset). Reassign via Projects page.</li>
                  <li>Cannot be undone — re-invite creates a fresh account.</li>
                </ul>
                <div className="flex gap-3 pt-2">
                  <button data-testid="delete-user-cancel" disabled={deleteBusy} onClick={() => setDeleteFor(null)} className="flex-1 rounded-full border border-black/15 py-2.5 text-sm font-semibold hover:bg-cream-2 disabled:opacity-50">Keep user</button>
                  <button data-testid="delete-user-confirm" disabled={deleteBusy} onClick={confirmDelete} className="flex-1 rounded-full bg-red-600 text-white py-2.5 text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
                    {deleteBusy ? "Deleting…" : "Yes, delete permanently"}
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="rounded-[1.5rem] border border-black/10 max-w-md" data-testid="invite-manager-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl text-royal-800">Invite {inviteRole === "TeamLeader" ? "Operation Head" : "Manager"}</DialogTitle></DialogHeader>
          <form onSubmit={submitInvite} className="space-y-3">
            <div><label className="label-caps block mb-1">Name</label>
              <Input data-testid="invite-manager-name" required value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className="rounded-lg border-black/20 h-11" />
            </div>
            <div><label className="label-caps block mb-1">Email</label>
              <Input data-testid="invite-manager-email" required type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="rounded-lg border-black/20 h-11" />
            </div>
            {inviteRole === "TeamLeader" && (
              <div><label className="label-caps block mb-1">Designation</label>
                <Input data-testid="invite-designation" value={invite.designation || ""} onChange={(e) => setInvite({ ...invite, designation: e.target.value })} className="rounded-lg border-black/20 h-11"
                  placeholder="e.g. Publication Head, Writing Head, Operation Head" />
                <div className="text-[10px] text-neutral-500 font-mono-tight mt-1">Shown as their title across the app. Defaults to "Operation Head" if left blank.</div>
              </div>
            )}
            <div className="text-xs text-neutral-500 font-mono-tight">
              User Limit: Unlimited
            </div>
            <DialogFooter>
              <button data-testid="invite-manager-submit" type="submit" className="btn-primary">Send Invite →</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
